
import WDElement from '../wd-element.js'
import WDDropdown from '../dropdown/wd-dropdown';

export default class WDButton extends WDElement
{

	#label = null;
	#icon = null;
	#suffix = null;
	#curState = {};
	#wrapper = null;
	#dropdown = null;
	#model = null;
	#themes = ['btn-clear', 'btn-primary', 'btn-secondary', 'btn-success', 'btn-warning', 'btn-danger'];
	#sizes = ['btn-xs', 'btn-sm', 'btn-md', 'btn-lg', 'btn-xl'];
	#config = {
		indicator: 'fa-solid fa-caret-down',
		split: false
	};

	constructor(params)
	{
		super().setAttribute('type', 'button');
		this.setAttribute('role','button');
		this.classList.add('btn');
		this.addEventListener('click', this.checkDisabled);

		if (params !== undefined) this.addConfig(params);
	};

	//set these up as accessors in case there's stuff we want to do later
	get model() { return this.#model; };
	set model(val) { this.#model = val; };

	addConfig(params)
	{
		if (typeof (params) == 'string')
		{
			this.#config.label = params;
		}
		else
		{
			//some custom types for quick-setting button configs
			if (params?.type) this.mergeConfigTypes(params);

			//this allows for specific overwriting of the above types
			Object.assign(this.#config, params);
		}

		//convert some parameters to include additional properties
		if (this.#config?.label && !this.#config?.title) this.#config.title = this.#config.label;
		if (this.#config?.fasIcon) this.#config.icon = `fa-solid fa-${this.#config.fasIcon}`;
		if (this.#config?.farIcon) this.#config.icon = `fa-solid fa-${this.#config.farIcon}`;

		//setup the button		
		if (this.#config?.title) this.title = this.#config.title;
		if (this.#config?.label) this.label = this.#config.label;
		if (this.#config?.responsiveLabel) this.responsiveLabel = this.#config.responsiveLabel;
		if (this.#config?.icon) this.icon = this.#config.icon;
		if (this.#config?.theme) this.theme = this.#config.theme;
		if (this.#config?.size) this.size = this.#config.size;
		if (this.#config?.suffix) this.suffix = this.#config.suffix;

		//event handlers
		if (this.#config?.click) this.on('click', this.#config.click);
		if (this.#config?.press) this.on('press', this.#config.press);
		if (this.#config?.touchstart) this.on('touchstart', this.#config.touchstart);
		if (this.#config?.touchend) this.on('touchend', this.#config.touchend);
	};

	mergeConfigTypes(params)
	{
		if (params.type == 'save')
		{
			this.#config.label = 'Save';
			this.#config.fasIcon = 'save';
			this.#config.theme = 'success';
		}

		if (params.type == 'delete')
		{
			this.#config.label = 'Delete';
			this.#config.fasIcon = 'trash';
			this.#config.theme = 'danger';
		}

		if (params.type == 'add')
		{
			this.#config.label = 'Add';
			this.#config.fasIcon = 'add';
			this.#config.theme = 'primary';
		}
	}

	get label()
	{
		if (!this.#label)
		{
			this.#label = wdc('<label class="label"/>');

			if (this.#icon) this.#icon.after(this.#label);
			else this.prepend(this.#label);
		}
		return this.#label;
	};

	set label(str)
	{
		this.label.replaceChildren(str);
	};

	set responsiveLabel(val)
	{
		this.classList.add('has-responsive-label');

		if (typeof val === 'string')
		{
			const lbl = wdc(`<span class="responsive-label">${val}</span>`);
			this.append(lbl);
		}
	};

	set icon(str)
	{
		if (!this.#icon)
		{
			this.#icon = wdc('<span class="icon"/>');
			this.prepend(this.#icon);
		}

		const iconClasses = Array.isArray(str) ? str : str.split(' ');
		iconClasses.push('icon');

		this.#icon.setAttribute('class', '');
		this.#icon.classList.add(...iconClasses);
	};

	set fasIcon(iconStr) { this.icon = `fa-solid fa-${iconStr}`; }
	set farIcon(iconStr) { this.icon = `fa-regular fa-${iconStr}`; }

	set theme(str)
	{
		this.classList.remove(...this.#themes);
		this.classList.add(`btn-${str}`);
	};

	get theme()
	{
		const currentTheme = this.#themes.find(theme => this.classList.contains(theme));
		return currentTheme !== undefined ? currentTheme.replace('btn-', '') : null;
	}

	set size(str)
	{
		this.classList.remove(...this.#sizes);
		this.classList.add(`btn-${str}`);
	};

	get size()
	{
		const currentSize = this.#sizes.find(size => this.classList.contains(size));
		return currentSize !== undefined ? currentSize.replace('btn-', '') : null;
	};

	get suffix()
	{
		if (!this.#suffix)
		{
			this.#suffix = wdc('<span class="suffix"/>');
			this.append(this.#suffix);
		}

		return this.#suffix;
	};

	set suffix(str)
	{
		this.suffix.setAttribute('class', `suffix ${str}`);
	};

	set busy(mode)
	{
		if (mode === true && !this.disabled)
		{
			//disable the button
			this.disabled = true;

			if (this.#icon)
			{
				this.#curState.icon = this.#icon.getAttribute('class');
				this.#icon.setAttribute('class', 'fa-solid fa-circle-notch fa-spin');
			}
		}
		else if (this.disabled)
		{
			this.disabled = false;
			if (this.#icon) this.#icon.setAttribute('class', this.#curState.icon);
			this.#curState = {};
		}

	};

	// Property getter
	get disabled()
	{
		return this.hasAttribute('disabled');
	}

	// Property setter
	set disabled(value)
	{
		if (value)
		{
			this.setAttribute('disabled', '');
		} else
		{
			this.removeAttribute('disabled');
		}
	}

	/**
	 * called before any click event to make sure the button isn't disabled.
	 * stops the action if it is
	 * 
	 * @param {*} event 
	 */
	checkDisabled(event) 
	{
		if (this.disabled)
		{
			// Prevent default action and stop propagation if disabled
			event.preventDefault();
			event.stopPropagation();
		}
	}

	/**
	 * dropdowns use these
	 */
	get wrapper()
	{
		//add the wrapper if it doesn't exist.  We sit inside it, so 
		//add adjacent 
		if (!this.#wrapper)
		{
			this.#wrapper = wdc('<div class="btn-group"/>');
			this.after(this.#wrapper);
			this.#wrapper.append(this);
		}

		return this.#wrapper;
	};

	get dropdown()
	{
		if (!this.#dropdown) this.addDropdown();
		return this.#dropdown;
	}

	set dropdown(val)
	{
		if (val == true) this.addDropdown();
		else if (this.#dropdown) this.#dropdown.remove();	//untested
	}

	addDropdown()
	{
		//make the dropdown
		this.#dropdown = new WDDropdown();
		this.wrapper.append(this.#dropdown);

		const icons = Array.isArray(this.#config?.indicator) ? this.#config.indicator : this.#config.indicator.split(' ');
		const ddIcon = icons.join(' ');

		//if it's a split button, make a new button to manage the dropdown
		if (this.#config.split == true)
		{
			const splitBtn = new WDButton({ icon: ddIcon });
			splitBtn.classList.add(this.getAttribute('class'));	//match the class list for the main button
			splitBtn.classList.add('dropdown-toggle', 'dropdown-toggle-split');
			splitBtn.setAttribute("data-bs-toggle", "dropdown");

			this.wrapper.append(splitBtn);
		}
		else
		{
			//setup the element to activate us when clicked
			this.append(wdc(`<span class="dropdown-indicator ${ddIcon}"/>`));
			this.classList.add("dropdown-toggle");
			this.setAttribute("data-bs-toggle", "dropdown");
		}

		this.on('show.bs.dropdown', ev => this.trigger('dropdown:show'));
		this.on('shown.bs.dropdown', ev => this.trigger('dropdown:shown'));
		this.on('hide.bs.dropdown', ev => this.trigger('dropdown:hide'));
		this.on('hidden.bs.dropdown', ev => this.trigger('dropdown:hidden'));

		return this.#dropdown;
	};

};

customElements.define('wd-button', WDButton);
